// src/hr/portal/ProfileLists.js -- the profile's Policies, Checklists, Reminders and Change log tabs.
// Policies and reminders are managed on their own pages; here HR sees this person's slice. Checklist
// items can be ticked in place (each tick records who and when). The change log shows the latest
// changes and can load the older ones.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiBell, FiCheck, FiClipboard, FiFileText, FiList } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Pill } from '../kit';
import { dateOfTimestamp } from '../timeoff/dates';
import { fullDate } from './portalUtils';
import { ChangeLogList } from './ProfileTimeOff';

const ROLE_WORDS = { hr: 'HR', it: 'IT', manager: 'Manager', employee: 'Employee' };
const KIND_WORDS = { onboarding: 'Onboarding', offboarding: 'Offboarding' };

export function ProfilePolicies({ data }) {
  const policies = data.policies || [];
  return (
    <Card delay={1} as="section" aria-label="Policies" title="Policies" className="hrp-pfl"
      action={<Link to="../policies">Manage policies <FiArrowRight aria-hidden="true" /></Link>}
    >
      {policies.length === 0 ? (
        <EmptyState compact icon={FiFileText} title="No policies assigned">Policies assigned to everyone, or to them, show up here.</EmptyState>
      ) : (
        <ul className="hrp-pfl__list">
          {policies.map((p) => {
            const signed = dateOfTimestamp(p.ackAt);
            return (
              <li key={p.id} className="hrp-pfl__it">
                <span className="hrp-pfl__ic" data-done={signed ? 'true' : undefined} aria-hidden="true">{signed ? <FiCheck /> : <FiFileText />}</span>
                <div className="hrp-pfl__txt"><b>{`${p.title} v${p.version}`}</b></div>
                {signed ? <Pill tone="ok">{`Signed ${fullDate(signed)}`}</Pill> : <Pill tone="warn">Not signed yet</Pill>}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ChecklistCard({ checklist, today, delay, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const items = checklist.items || [];
  const done = items.filter((i) => i.done).length;
  const share = items.length ? done / items.length : 0;

  const tick = async (item) => {
    setBusy(item.id);
    setError('');
    try {
      const result = await hr.admin.checklists.setItem(checklist.id, item.id, { done: !item.done });
      onChanged((result && result.checklist) || null);
    } catch (err) {
      setError(errorText(err, "Couldn't save that. Try again."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card delay={delay} as="section" aria-label={`${KIND_WORDS[checklist.kind] || 'Checklist'} checklist`} className="hrp-ck">
      <div className="hrp-card__head">
        <h2 className="hrp-card__title">{`${KIND_WORDS[checklist.kind] || 'Checklist'} checklist`}</h2>
        {checklist.completedAt ? <Pill tone="ok" icon={<FiCheck />}>Complete</Pill> : <span className="hrp-ck__count">{`${done} of ${items.length} done`}</span>}
      </div>
      <div className="hrp-ck__prog" aria-hidden="true"><span style={{ width: `${Math.round(share * 100)}%` }} /></div>
      <ul className="hrp-ck__list">
        {items.map((item) => {
          const overdue = !item.done && item.dueDate && item.dueDate < today;
          const when = dateOfTimestamp(item.doneAt);
          return (
            <li key={item.id} className="hrp-ck__it" data-done={item.done ? 'true' : undefined}>
              <button
                type="button"
                className="hrp-ck__box"
                role="checkbox"
                aria-checked={!!item.done}
                aria-label={item.label}
                disabled={busy === item.id}
                onClick={() => tick(item)}
              >
                {item.done && <FiCheck aria-hidden="true" />}
              </button>
              <span className="hrp-ck__tx">{item.label}</span>
              <Pill tone="mute">{ROLE_WORDS[item.role] || 'HR'}</Pill>
              <span className="hrp-ck__by" data-overdue={overdue ? 'true' : undefined}>
                {item.done && when ? `Done ${fullDate(when)}` : null}
                {!item.done && item.dueDate ? `${overdue ? 'Overdue · ' : 'Due '}${fullDate(item.dueDate)}` : null}
              </span>
            </li>
          );
        })}
      </ul>
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </Card>
  );
}

export function ProfileChecklists({ data, today, onToast, onReload, onPatch }) {
  const checklists = data.checklists || [];
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const openKinds = new Set(checklists.filter((c) => !c.completedAt).map((c) => c.kind));

  const start = async (kind) => {
    setBusy(kind);
    setError('');
    try {
      await hr.admin.checklists.start(data.person.id, { kind });
      onToast(`${KIND_WORDS[kind]} checklist started`);
      onReload();
    } catch (err) {
      setError(errorText(err, "Couldn't start the checklist. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const startButtons = ['onboarding', 'offboarding'].filter((k) => !openKinds.has(k)).map((k) => (
    <Btn key={k} variant="ghost" size="sm" icon={FiClipboard} loading={busy === k} onClick={() => start(k)}>{`Start ${k} checklist`}</Btn>
  ));

  return (
    <div className="hrp-stack">
      {checklists.length === 0 ? (
        <Card delay={1}>
          <EmptyState compact icon={FiList} title="No checklists yet" action={<div className="hrp-row">{startButtons}</div>}>
            Each ticked item records who ticked it and when.
          </EmptyState>
        </Card>
      ) : (
        <>
          {checklists.map((c, i) => (
            <ChecklistCard
              key={c.id}
              checklist={c}
              today={today}
              delay={i + 1}
              onChanged={(next) => onPatch((d) => ({ ...d, checklists: d.checklists.map((x) => (x.id === c.id && next ? next : x)) }))}
            />
          ))}
          {startButtons.length > 0 && <div className="hrp-row">{startButtons}</div>}
        </>
      )}
      {error && <div className="hrp-form-err" role="alert"><FiAlertCircle aria-hidden="true" /><span>{error}</span></div>}
    </div>
  );
}

export function ProfileReminders({ data }) {
  const reminders = data.reminders || [];
  return (
    <Card delay={1} as="section" aria-label="Reminders" title="Reminders" className="hrp-pfl"
      action={<Link to="../reminders">Add or change in Reminders <FiArrowRight aria-hidden="true" /></Link>}
    >
      {reminders.length === 0 ? (
        <EmptyState compact icon={FiBell} title="No reminders">Work visas, probation ends, certifications — HR gets emails before they're due.</EmptyState>
      ) : (
        <ul className="hrp-pfl__list">
          {reminders.map((r) => {
            const over = !r.done && r.daysLeft !== null && r.daysLeft < 0;
            let when = r.dueDate ? fullDate(r.dueDate) : 'No date';
            if (!r.done && r.daysLeft !== null) when += over ? ` · ${Math.abs(r.daysLeft)} days overdue` : ` · in ${r.daysLeft} days`;
            return (
              <li key={r.id} className="hrp-pfl__it">
                <span className="hrp-pfl__ic" data-done={r.done ? 'true' : undefined} aria-hidden="true">{r.done ? <FiCheck /> : <FiBell />}</span>
                <div className="hrp-pfl__txt">
                  <b>{r.label}</b>
                  <span>{r.kind === 'workAuth' && r.docType ? `${r.docType} · ${when}` : when}</span>
                </div>
                {r.done ? <Pill tone="ok">Done</Pill> : <Pill tone={over ? 'bad' : 'warn'}>{over ? 'Overdue' : 'Open'}</Pill>}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export function ProfileChangeLog({ data, types }) {
  const typesById = useMemo(() => new Map((types || []).map((t) => [t.id, t])), [types]);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shown = rows || data.changelog || [];

  const more = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.changelog({ employeeId: data.person.id, limit: 200 });
      setRows((result && result.changelog) || []);
    } catch (err) {
      setError(errorText(err, "Couldn't load older changes. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card delay={1} as="section" aria-label="Change log" title="Change log" className="hrp-logcard hrp-logcard--full">
      <p className="hrp-pfl__sub">Every change HR makes to this person, newest first. Only HR can see this.</p>
      {shown.length === 0 ? (
        <EmptyState compact title="No changes yet">Edits to their record, allowances and time off are logged here.</EmptyState>
      ) : (
        <ChangeLogList rows={shown} typesById={typesById} />
      )}
      {!rows && (data.changelog || []).length >= 20 && (
        <div className="hrp-pfl__more"><Btn variant="ghost" size="sm" loading={busy} onClick={more}>Show older changes</Btn></div>
      )}
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </Card>
  );
}
